import { prepareFactors } from '../helpers';

export const joinArray = (arr: string[]) => arr?.join(', ');

type TReadingName =
  | 'blood pressure'
  | 'pulse'
  | 'SpO2'
  | 'weight'
  | 'blood glucose'
  | 'oxygen';

interface Q1Data {
  reading: string;
  dateOfReading?: string;
  patient?: string | null;
  speakWithOther?: string | null;
  relationship: string | null;
  readingName: TReadingName;
}

export const Q1Blurb = (data: Q1Data) => {
  let message = '';
  const {
    reading,
    dateOfReading,
    relationship,
    speakWithOther,
    patient,
    readingName,
  } = data;

  message += `Nurse called patient for a critical ${readingName} level of ${reading} on ${dateOfReading}. `;
  if (patient) {
    message += `Spoke with ${patient} and notified them they are on a recorded line for quality and compliance purposes. `;
  }
  if (speakWithOther) {
    message += `Spoke with ${relationship} and notified them they are on a recorded line for quality and compliance purposes. Name of alternate contact: ${speakWithOther}, ${relationship}. `;
  }

  return message;
};

export const Q2Blurb = (speakerStatement: string, AOC: string) => {
  let message = '';

  message += speakerStatement;
  message += ` is alert and oriented ${AOC}. `;

  return message;
};

export const Q3Blurb = (
  speakerStatement: string,
  next: string | null,
  last: string | null,
) => {
  let message = '';

  if (last && next) {
    message += speakerStatement;
    message += ` was last seen on ${last} and has an upcoming appointment on ${next}. `;
  } else {
    if (last) {
      message += speakerStatement;
      message += ` was last seen by their provider on ${last}. `;
    }
    if (next) {
      message += speakerStatement;
      message += ` next provider appointment is scheduled for ${next}. `;
    }
  }

  return message;
};

interface Q4Data {
  isOneMoreReading: string | null;
  bounds: boolean | null;
  repeatReadingWas: string | null;
}
export const Q4Blurb = (speakerStatement: string, data: Q4Data) => {
  const { isOneMoreReading, bounds, repeatReadingWas } = data;
  let message = '';

  if (isOneMoreReading) {
    message += speakerStatement;
    message += isOneMoreReading === 'true' ? ' is' : ' is not';
    message += ' able to take another reading at the time of call. ';
  }
  if (bounds !== null) {
    message += `The repeat reading ${bounds ? 'is' : 'is not'} out of bounds. `;
  }
  if (repeatReadingWas) {
    message += `The repeat reading was: ${repeatReadingWas}. `;
  }

  return message;
};

interface MedicationBlurb {
  isTakeMedications: string | null;
  speakerStatement: string;
  speakWithPatient: boolean;
  medications?: string[] | null;
  type: 'any PRN' | 'routine';
}

export const medicationBlurb = (data: MedicationBlurb) => {
  const {
    type,
    isTakeMedications,
    speakerStatement,
    speakWithPatient,
    medications,
  } = data;
  let message = '';

  if (isTakeMedications === 'true') {
    if (!medications || medications.length === 0) {
      message += speakerStatement;
      message += speakWithPatient ? ' confirms taking' : ' took';
      message += ` the following ${type} medications as prescribed. `;
    } else {
      message += speakerStatement;
      message += speakWithPatient ? ' confirms taking' : ' took';
      message += ` the following ${type} medications: `;
      message += `${joinArray(medications)}. `;
    }
  }

  if (isTakeMedications === 'false') {
    message += speakerStatement;
    message += speakWithPatient ? ' denies taking' : ' did not take';
    message += ` ${type} medications. `;
  }

  return message;
};

interface FactorsBlurb {
  speakerStatement: string;
  confirmedFactors: string[];
  deniedFactors: string[];
  readingName: TReadingName;
}
export const factorsBlurb = (data: FactorsBlurb) => {
  const { speakerStatement, confirmedFactors, deniedFactors, readingName } =
    data;
  let message = '';

  message += `Nurse assessed factors that may impact ${readingName} readings. `;
  if (confirmedFactors?.length > 0) {
    const preparedFactors = prepareFactors(confirmedFactors);
    message += speakerStatement;
    message += ` confirms the following: ${joinArray(preparedFactors)}. `;
  }
  if (deniedFactors?.length > 0) {
    const preparedFactors = prepareFactors(deniedFactors);
    message += speakerStatement;
    message += ` denies the following: ${joinArray(preparedFactors)}. `;
  }

  return message;
};

interface NursingInterventionBlurb {
  escalationToClinic: string[];
  notEscalated: string[];
  interventionDetails: string | null;
  emergencyRoom?: boolean;
  escalation?: boolean;
  nursingEducation?: boolean;
}

export const nursingInterventionBlurb = (data: NursingInterventionBlurb) => {
  const {
    escalationToClinic,
    notEscalated,
    interventionDetails,
    emergencyRoom,
    escalation,
    nursingEducation,
  } = data;

  let message = '';

  if (emergencyRoom) {
    message += `Patient requires immediate care. Nurse provided emergency assistance to patient. `;
  }
  if (escalation) {
    message += `Patient requires provider intervention. Nurse escalated the reading to the clinic according to run book protocol. `;
    if (escalationToClinic?.length > 0) {
      message += ` Nurse escalated the reading due to: ${joinArray(
        escalationToClinic,
      )}. `;
    }
  }
  if (nursingEducation) {
    message += `Nurse provided education to patient. `;
  }
  if (notEscalated?.length > 0) {
    message += `Reading not escalated due to: ${joinArray(notEscalated)}. `;
  }
  if (interventionDetails) {
    message += `${interventionDetails}. `;
  }

  return message;
};

export const verbalizedBlurb = (
  speakerStatement: string,
  verbalized: boolean | null,
) => {
  const message = `${speakerStatement} verbalized understanding and ${
    verbalized ? 'is' : 'is not'
  } agreeable to nursing recommendation.`;

  return message;
};

interface SymptomsBlurb {
  speakerStatement: string;
  confirmedEmergentSymptoms: string[];
  confirmedSymptoms: string[];
  isAllSymptomsDenied: boolean;
  emergencyModalAnswer: boolean | null;
  speakWith: string;
  deniedSymptoms: string[];
  symptomsOnset: string | null;
  speakWithPatient: boolean;
}

export const symptomsBlurb = (data: SymptomsBlurb) => {
  const {
    speakerStatement,
    confirmedEmergentSymptoms,
    confirmedSymptoms,
    isAllSymptomsDenied,
    emergencyModalAnswer,
    speakWith,
    deniedSymptoms,
    speakWithPatient,
    symptomsOnset,
  } = data;

  let message = '';
  const allConfirmedSymptoms = [
    ...confirmedEmergentSymptoms,
    ...confirmedSymptoms,
  ];

  const isConfirmedSymptomsEmergency =
    emergencyModalAnswer === true && confirmedEmergentSymptoms?.length > 0;

  if (isConfirmedSymptomsEmergency) {
    message += speakerStatement;
    message += ` confirms emergent symptoms ${joinArray(
      allConfirmedSymptoms,
    )}. `;
  }

  if (isAllSymptomsDenied) {
    message += speakWith;
    message += speakWithPatient
      ? ' denies any'
      : ' states that patient is not having';
    message += ' symptoms at this time. ';
  }

  if (!isConfirmedSymptomsEmergency && !isAllSymptomsDenied) {
    if (allConfirmedSymptoms?.length > 0) {
      message += speakerStatement;
      message += ` confirms ${joinArray(allConfirmedSymptoms)}. `;
    }
    if (deniedSymptoms?.length > 0) {
      message += speakerStatement;
      message += ` denies ${joinArray(deniedSymptoms)}. `;
    }
  }

  if (symptomsOnset) {
    message += `${symptomsOnset}. `;
  }

  return message;
};
